import React, { useState, useEffect } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { Dialog, DialogActions, DialogContent, Button, DialogTitle, Grid, TextField, Switch, FormControlLabel } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { updateLocationScedule } from '../../../services/LocationService';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const daysOfWeek = [
  { name: 'Lunes', value: 1 },
  { name: 'Martes', value: 2 },
  { name: 'Miércoles', value: 3 },
  { name: 'Jueves', value: 4 },
  { name: 'Viernes', value: 5 },
  { name: 'Sábado', value: 6 },
  { name: 'Domingo', value: 0 }
];

const EditScheduleLocationModal = ({ open, onClose, location, onSave }) => {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [schedule, setSchedule] = useState(() =>
    daysOfWeek.map(day => ({
      day_of_week: day.value,
      start_hour: null,
      end_hour: null,
      active: false
    }))
  );

  const convertToDayjs = (timeString) => {
    if (!timeString) return null;
    return dayjs(timeString, "HH:mm:ss");
  };

  useEffect(() => {
    if (open && location?.schedule) {
      const formattedSchedule = daysOfWeek.map(day => {
        const locationDay = location.schedule.find(s => s.dayOfWeek === day.value);
        return {
          day_of_week: day.value,
          start_hour: locationDay?.startHour ? convertToDayjs(locationDay.startHour) : null,
          end_hour: locationDay?.endHour ? convertToDayjs(locationDay.endHour) : null,
          active: !!locationDay
        };
      });
      setSchedule(formattedSchedule);
    }
  }, [open, location]);

  const handleTimeChange = (time, index, type) => {
    setSchedule(prevSchedule => {
      const newSchedule = [...prevSchedule];
      newSchedule[index][type] = time || null;
      return newSchedule;
    });
  };

  const handleSwitchChange = (index, isActive) => {
    setSchedule(prevSchedule => {
      const newSchedule = [...prevSchedule];
      newSchedule[index].active = isActive;
      if (!isActive) {
        newSchedule[index].start_hour = null;
        newSchedule[index].end_hour = null;
      }
      return newSchedule;
    });
  };

  const isValidActiveRange = (startHour, endHour) => {
    if (!startHour || !endHour) return false;
    return !dayjs(startHour).isSame(endHour);
  };

  const saveSchedule = async () => {
    setLoading(true);
    const isValid = schedule.every(({ active, start_hour, end_hour }) => {
      if (!active) return true;
      return isValidActiveRange(start_hour, end_hour);
    });
    if (!isValid) {
      alert('Por favor, asegúrate de que todos los horarios estén completos y que inicio y fin no sean iguales. Si el fin es menor al inicio, se interpreta como cierre del día siguiente.');
      setLoading(false);
      return;
    }

    const formattedSchedule = schedule
      .filter(({ active, start_hour, end_hour }) => active && start_hour && end_hour)
      .map(({ day_of_week, start_hour, end_hour }) => ({
        day_of_week: day_of_week,
        start_hour: dayjs(start_hour).format("HH:mm:ss"),
        end_hour: dayjs(end_hour).format("HH:mm:ss")
      }));

    try {
      const updatedLocation = await updateLocationScedule(location.id, formattedSchedule);
      onSave(updatedLocation);
      onClose();
    } catch (error) {
      console.error('Error updating schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('edit_schedule_location')}</DialogTitle>
      <DialogContent style={{ padding: "50px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            {daysOfWeek.map((day, index) => (
              <Grid container spacing={2} style={{ paddingBottom: "16px" }} key={day.value} alignItems="center">
                <Grid item xs={2}>
                  <span>{day.name}</span>
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedule[index].active}
                        onChange={(event) => handleSwitchChange(index, event.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TimePicker
                    label="Inicio"
                    value={schedule[index].start_hour}
                    onChange={(newValue) => handleTimeChange(newValue, index, 'start_hour')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    disabled={!schedule[index].active}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TimePicker
                    label="Fin"
                    value={schedule[index].end_hour}
                    onChange={(newValue) => handleTimeChange(newValue, index, 'end_hour')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    disabled={!schedule[index].active}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} onClick={saveSchedule} variant="contained">
          {t('update')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleLocationModal;
